import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CategoryBadge from '../../components/candidates/CategoryBadge';
import Can from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { useAuth } from '../../auth/AuthContext';
import { listLeads, deleteLead, convertLead } from '../../api/leads';
import { listWorkflowCategories } from '../../api/workflow';
import { listLeadSources } from '../../api/masters';
import { pushToast } from '../../components/ui/toastBus';
import { formatValidationErrors } from '../../utils/errors';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'converted', label: 'Converted' },
];

export default function LeadsList() {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const { user } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });
  const sourcesQuery = useQuery({ queryKey: ['lead-sources', { include_inactive: true }], queryFn: () => listLeadSources({ include_inactive: true }) });

  const params = {
    search: search || undefined,
    status: status || undefined,
    workflow_category_id: categoryId || undefined,
    lead_source_id: sourceId || undefined,
    page,
  };

  const leadsQuery = useQuery({ queryKey: ['leads', params], queryFn: () => listLeads(params) });

  const deleteMutation = useMutation({
    mutationFn: deleteLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      pushToast({ type: 'success', message: 'Lead deleted.' });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete lead.' });
      setDeleteTarget(null);
    },
  });

  const sendMutation = useMutation({
    mutationFn: convertLead,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      const missing = result.missing_documents;
      pushToast({
        type: missing?.length ? 'error' : 'success',
        message: missing?.length
          ? `Sent to candidate ${result.candidate.reference_no}, but ${missing.length} document(s) are still missing.`
          : `Sent to candidate ${result.candidate.reference_no}.`,
      });
      setSendTarget(null);
    },
    onError: (err) => {
      const message = err.response?.data?.errors
        ? `Complete the profile before sending — ${formatValidationErrors(err.response.data.errors)}`
        : err.response?.data?.message || 'Could not send lead.';
      pushToast({ type: 'error', message });
      setSendTarget(null);
    },
  });

  const canSend = (row) => can('leads.send-all') || (can('leads.send-own') && row.created_by_id === user?.id);
  const canDelete = (row) => can('leads.delete-all') || (can('leads.delete-own') && row.created_by_id === user?.id);

  const columns = [
    {
      key: 'reference_no',
      label: 'Reference No',
      render: (row) => (
        <Link to={`/leads/${row.id}`} className="font-medium text-primary hover:underline">
          {row.reference_no}
        </Link>
      ),
    },
    { key: 'full_name', label: 'Full Name' },
    { key: 'contact_no', label: 'Contact No' },
    { key: 'category', label: 'Category', render: (row) => (row.category ? <CategoryBadge category={row.category} /> : <span className="text-gray-400">—</span>) },
    { key: 'source', label: 'Source', render: (row) => row.source || <span className="text-gray-400">—</span> },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <Badge tone={row.status === 'converted' ? 'green' : 'gray'}>{row.status}</Badge>,
    },
    { key: 'created_by', label: 'Created By' },
    { key: 'created_at', label: 'Created', render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Link to={`/leads/${row.id}`} className="text-primary hover:underline">
            View
          </Link>
          {row.status === 'new' && canSend(row) && (
            <button onClick={() => setSendTarget(row)} className="text-gray-600 hover:underline">
              Send to Candidate
            </button>
          )}
          {canDelete(row) && (
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leads"
        actions={
          <Can perm="leads.create">
            <Link to="/leads/new">
              <Button>Register Lead</Button>
            </Link>
          </Can>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => {
              setStatus(tab.value);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              status === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          placeholder="Search name, reference, passport, contact…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72"
        />
        <Select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            setPage(1);
          }}
          className="w-56"
        >
          <option value="">All categories</option>
          {(categoriesQuery.data || []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          value={sourceId}
          onChange={(e) => {
            setSourceId(e.target.value);
            setPage(1);
          }}
          className="w-48"
        >
          <option value="">All sources</option>
          {(sourcesQuery.data || []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <Table
        columns={columns}
        data={leadsQuery.data?.data || []}
        isLoading={leadsQuery.isLoading}
        pagination={leadsQuery.data}
        onPageChange={setPage}
        emptyMessage="No leads found."
      />

      <ConfirmDialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onConfirm={() => sendMutation.mutate(sendTarget.id)}
        title="Send to Candidate"
        message={`Send ${sendTarget?.full_name} (${sendTarget?.reference_no}) to Candidate? This creates a candidate record and starts the workflow.`}
        confirmLabel="Send"
        variant="primary"
        loading={sendMutation.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete lead"
        message={`Delete ${deleteTarget?.full_name} (${deleteTarget?.reference_no})? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
